import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/Icon';
import { blogAPI, authAPI } from 'utils/api';

const CATS = ['Technology','Web Development','Python','Cyber Security','AI & ML','General'];
const EMPTY = { title:'', content:'', category:'General', tags:'', metaTitle:'', metaDescription:'', published:true };

export default function AdminDashboard() {
  const [blogs, setBlogs]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView]       = useState('list');
  const [form, setForm]       = useState(EMPTY);
  const [editId, setEditId]   = useState(null);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState({ type:'', text:'' });
  const nav = useNavigate();

  useEffect(() => {
    authAPI.verify().catch(() => {
      localStorage.removeItem('dl_token');
      nav('/dv-panel-83ca0bcf');
    });
    fetchBlogs();
  }, []);

  const fetchBlogs = () => {
    setLoading(true);
    blogAPI.adminAll().then(r => setBlogs(r.data.blogs)).catch(() => setBlogs([])).finally(() => setLoading(false));
  };

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type:'', text:'' }), 4000); };

  const logout = () => { localStorage.removeItem('dl_token'); nav('/dv-panel-83ca0bcf'); };

  const startEdit = blog => {
    setForm({ ...blog, tags: blog.tags?.join(', ') || '' });
    setEditId(blog._id); setView('edit'); window.scrollTo(0, 0);
  };

  const del = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try { await blogAPI.remove(id); showMsg('success','Post deleted.'); fetchBlogs(); }
    catch { showMsg('error','Delete failed.'); }
  };

  const save = async e => {
    e.preventDefault();
    if (!form.title.trim()) { showMsg('error','Title is required.'); return; }
    if (!form.content.trim()) { showMsg('error','Content is required.'); return; }
    setSaving(true);
    try {
      const payload = { ...form, tags: form.tags ? form.tags.split(',').map(t=>t.trim()).filter(Boolean) : [] };
      if (view === 'edit') { await blogAPI.update(editId, payload); showMsg('success','Post updated!'); }
      else { await blogAPI.create(payload); showMsg('success','Post published!'); }
      setView('list'); setForm(EMPTY); setEditId(null); fetchBlogs();
    } catch (err) { showMsg('error', err.response?.data?.error || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const back = () => { setView('list'); setForm(EMPTY); setEditId(null); };
  const upd  = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="admin-pg">
      <div className="container">
        {/* Header — no email shown, just role */}
        <div className="admin-hdr">
          <div>
            <h1>Blog Management</h1>
            <p style={{ color:'var(--slate-400)', fontSize:14, marginTop:4 }}>
              {blogs.length} post{blogs.length !== 1 ? 's' : ''} total
            </p>
          </div>
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            {view !== 'list' && (
              <button className="btn btn-secondary" onClick={back}>
                <Icon name="arrowLeft" size={16} />Back
              </button>
            )}
            {view === 'list' && (
              <button className="btn btn-primary" onClick={() => { setView('add'); setForm(EMPTY); }}>
                <Icon name="plus" size={16} />New Post
              </button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={logout}>
              <Icon name="logout" size={15} />Logout
            </button>
          </div>
        </div>

        {msg.text && (
          <div className={`alert alert-${msg.type}`} style={{ marginBottom:20 }}>
            <Icon name={msg.type==='success'?'checkCircle':'alertCircle'} size={16} />{msg.text}
          </div>
        )}

        {/* LIST */}
        {view === 'list' && (
          loading ? (
            <div className="loading"><div className="spinner" />Loading posts...</div>
          ) : blogs.length === 0 ? (
            <div style={{ textAlign:'center', padding:80, background:'var(--white)', border:'1.5px solid var(--slate-200)', borderRadius:'var(--r-lg)' }}>
              <div style={{ width:52, height:52, background:'var(--slate-100)', borderRadius:'var(--r)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px', color:'var(--slate-300)' }}>
                <Icon name="blog" size={24} />
              </div>
              <h3 style={{ marginBottom:8 }}>No posts yet</h3>
              <p style={{ color:'var(--slate-400)', marginBottom:20 }}>Create your first blog post.</p>
              <button className="btn btn-primary" onClick={() => setView('add')}><Icon name="plus" size={16} />Create First Post</button>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="data-tbl">
                <thead>
                  <tr><th>Title</th><th>Category</th><th>Status</th><th>Views</th><th>Date</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {blogs.map(b => (
                    <tr key={b._id}>
                      <td style={{ maxWidth:300 }}>
                        <strong style={{ display:'block', marginBottom:2, color:'var(--slate-800)', fontSize:14 }}>{b.title}</strong>
                        <span style={{ fontSize:12, color:'var(--slate-400)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', display:'block', maxWidth:280 }}>{b.excerpt}</span>
                      </td>
                      <td><span className="cat-badge">{b.category}</span></td>
                      <td>
                        <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:12, fontWeight:700, color:b.published?'var(--green)':'var(--slate-400)' }}>
                          <Icon name={b.published?'checkCircle':'info'} size={13} />
                          {b.published?'Published':'Draft'}
                        </span>
                      </td>
                      <td style={{ fontSize:13, color:'var(--slate-500)' }}>{b.views}</td>
                      <td style={{ fontSize:13, color:'var(--slate-400)', whiteSpace:'nowrap' }}>
                        {new Date(b.createdAt).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}
                      </td>
                      <td>
                        <div className="act-btns">
                          <button className="btn btn-secondary btn-sm" onClick={() => startEdit(b)}><Icon name="edit" size={13} />Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={() => del(b._id, b.title)}><Icon name="trash" size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {/* FORM */}
        {(view === 'add' || view === 'edit') && (
          <div className="tool-box" style={{ maxWidth:860 }}>
            <h2 style={{ fontSize:20, fontWeight:700, marginBottom:28, display:'flex', alignItems:'center', gap:10 }}>
              <Icon name={view==='edit'?'edit':'plus'} size={20} />
              {view==='edit'?'Edit Post':'Write New Post'}
            </h2>
            <form onSubmit={save}>
              <div className="form-group">
                <label>Title *</label>
                <input required className="form-control" placeholder="Enter a clear, descriptive title..." value={form.title} onChange={e => upd('title',e.target.value)} style={{ fontSize:16, fontWeight:500 }} />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                <div className="form-group">
                  <label>Category</label>
                  <select className="form-control" value={form.category} onChange={e => upd('category',e.target.value)}>
                    {CATS.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Tags <span className="hint">(comma-separated)</span></label>
                  <input className="form-control" placeholder="react, tips, tutorial" value={form.tags} onChange={e => upd('tags',e.target.value)} />
                </div>
              </div>
              {/* SEO */}
              <div style={{ background:'var(--blue-50)', border:'1.5px solid var(--blue-100)', borderRadius:'var(--r)', padding:'16px 18px', marginBottom:16 }}>
                <p style={{ fontSize:13, fontWeight:700, color:'var(--blue-dd)', marginBottom:14, display:'flex', alignItems:'center', gap:6 }}>
                  <Icon name="search" size={14} />SEO Settings
                </p>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label>Meta Title <span className="hint">(leave empty = post title)</span></label>
                    <input className="form-control" placeholder="Custom SEO title..." value={form.metaTitle} onChange={e => upd('metaTitle',e.target.value)} />
                  </div>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label>Meta Description <span className="hint">(max 160 chars)</span></label>
                    <input className="form-control" placeholder="Brief description for search results..." value={form.metaDescription} onChange={e => upd('metaDescription',e.target.value)} maxLength={160} />
                    <p style={{ fontSize:11, color:form.metaDescription.length>140?'var(--red)':'var(--slate-400)', marginTop:4 }}>{form.metaDescription.length}/160</p>
                  </div>
                </div>
              </div>
              {/* Content */}
              <div className="form-group">
                <label>Content * <span className="hint">(HTML supported)</span></label>
                <textarea
                  required className="form-control" rows={22}
                  placeholder={'<h2>Section Heading</h2>\n<p>Your paragraph here.</p>\n\n<h3>Sub Heading</h3>\n<p>More content with <strong>bold</strong> text.</p>\n\n<pre><code>// Code block\nconst x = 1;</code></pre>\n\n<ul>\n  <li>List item 1</li>\n  <li>List item 2</li>\n</ul>'}
                  value={form.content} onChange={e => upd('content',e.target.value)}
                  style={{ fontFamily:'var(--mono)', fontSize:13, lineHeight:1.65 }}
                />
                <p style={{ fontSize:11, color:'var(--slate-400)', marginTop:4 }}>
                  {form.content.length.toLocaleString()} characters · ~{Math.ceil(form.content.replace(/<[^>]+>/g,'').split(/\s+/).filter(Boolean).length/200)} min read
                </p>
              </div>
              {/* Published */}
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:24, padding:'12px 16px', background:'var(--slate-50)', borderRadius:'var(--r-sm)', border:'1px solid var(--slate-200)' }}>
                <input type="checkbox" id="pub" checked={form.published} onChange={e => upd('published',e.target.checked)} style={{ width:18, height:18, accentColor:'var(--blue)', cursor:'pointer' }} />
                <label htmlFor="pub" style={{ margin:0, cursor:'pointer', fontWeight:600, fontSize:14, display:'flex', alignItems:'center', gap:8 }}>
                  <Icon name="checkCircle" size={16} style={{ color:form.published?'var(--green)':'var(--slate-300)' }} />
                  {form.published?'Published — visible to all readers':'Draft — only in admin panel'}
                </label>
              </div>
              <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                <button type="submit" className="btn btn-primary btn-lg" disabled={saving} style={{ minWidth:180 }}>
                  {saving ? <><span className="spinner sp-sm" />Saving...</> : <><Icon name={view==='edit'?'check':'zap'} size={18} />{view==='edit'?'Update Post':'Publish Post'}</>}
                </button>
                <button type="button" className="btn btn-secondary btn-lg" onClick={back}>Cancel</button>
              </div>
            </form>
          </div>
        )}
        {/* Branding */}
        <p style={{ textAlign:'center', fontSize:12, color:'var(--slate-300)', marginTop:40, paddingTop:20, borderTop:'1px solid var(--slate-100)' }}>
          Developed by <strong>Ethoss Technologies</strong>
        </p>
      </div>
    </div>
  );
}
